/* eslint-disable no-restricted-imports */
import { memo, useEffect, useRef, useState } from 'react';

import { BOTTOM_NAVIGATION_ID, HEADER_ID } from '@/components/content/Header/constants';
import { stickyProductHeaderSX } from '@/components/content/ProductDetails/styles/StickyProductHeader/stickyProductHeader';
import { PRODUCT_BAR_ID, NAVIGATION_BAR_ID } from '@/components/content/ProductDetails/constants';
import { NavigationBar } from './NavigationBar';
import { ProductBar } from './ProductBar';
import { useMediaQuery, useTheme } from '@mui/material';

export const StickyProductHeader = memo(
	({
		navigationBarSentinelRef,
		productBarSentinelRef,
	}: {
		navigationBarSentinelRef: React.RefObject<HTMLElement>;
		productBarSentinelRef: React.RefObject<HTMLElement>;
	}) => {
		const theme = useTheme();
		const isSmScreen = useMediaQuery(theme.breakpoints.down('md'));

		const productBarRef = useRef<HTMLDivElement>(null);
		const navigationBarRef = useRef<HTMLDivElement>(null);

		const [isNavigationBarVisible, setIsNavigationBarVisible] = useState(false);
		const [isProductBarVisible, setIsProductBarVisible] = useState(false);

		const [navigationBarHeight, setNavigationBarHeight] = useState(0);
		const [productBarHeight, setProductBarHeight] = useState(0);
		const [headerHeight, setHeaderHeight] = useState(0);
		const [bottomNavigationHeight, setBottomNavigationHeight] = useState(0);

		// Observe the navigation bar sentinel (which is at the top of product details).
		// When the navigation bar sentinel is out of view, show the navigation bar.
		useEffect(() => {
			if (!navigationBarSentinelRef.current) return;

			const mdRootMargin = '0px 0px 0px 0px';
			const smRootMargin = `-${headerHeight}px 0px 0px 0px`;

			const observer = new IntersectionObserver(
				(entries) => {
					const [entry] = entries;
					setIsNavigationBarVisible(!entry.isIntersecting);
				},
				{
					threshold: [0],
					rootMargin: isSmScreen ? smRootMargin : mdRootMargin,
				}
			);

			observer.observe(navigationBarSentinelRef.current);

			return () => {
				observer.disconnect();
			};
		}, [headerHeight, isSmScreen, navigationBarSentinelRef]);

		// Observe the product bar sentinel (which is the whole product details).
		// When the product bar sentinel is out of view, show the product bar.
		useEffect(() => {
			if (!productBarSentinelRef.current) return;

			const mdRootMargin = `${-navigationBarHeight + productBarHeight}px 0px 0px 0px`;
			const smRootMargin = `${
				-headerHeight - navigationBarHeight + productBarHeight
			}px 0px 0px 0px`;

			const observer = new IntersectionObserver(
				(entries) => {
					const [entry] = entries;
					setIsProductBarVisible(!entry.isIntersecting);
				},
				{
					threshold: [0],
					rootMargin: isSmScreen ? smRootMargin : mdRootMargin,
				}
			);

			observer.observe(productBarSentinelRef.current);

			return () => {
				observer.disconnect();
			};
		}, [headerHeight, isSmScreen, navigationBarHeight, productBarHeight, productBarSentinelRef]);

		// Use a ResizeObserver to keep track of the heights of:
		// 1. Top header
		// 2. Bottom navigation
		// 3. Product bar
		// 4. Navigation bar
		// On resize, update the heights
		useEffect(() => {
			const observer = new ResizeObserver((entries) => {
				entries.forEach((entry) => {
					const id = entry.target.id;
					if (!id) return;

					const borderBoxSize = entry.borderBoxSize[0];
					if (!borderBoxSize) return;

					if (id === HEADER_ID) {
						setHeaderHeight(borderBoxSize.blockSize);
					}

					if (id === BOTTOM_NAVIGATION_ID) {
						setBottomNavigationHeight(borderBoxSize.blockSize);
					}

					if (id === PRODUCT_BAR_ID) {
						setProductBarHeight(borderBoxSize.blockSize);
					}

					if (id === NAVIGATION_BAR_ID) {
						setNavigationBarHeight(borderBoxSize.blockSize);
					}
				});
			});

			const header = document.getElementById(HEADER_ID);
			if (header) {
				observer.observe(header);
				setHeaderHeight(header.clientHeight);
			}

			const bottomNavigation = document.getElementById(BOTTOM_NAVIGATION_ID);
			if (bottomNavigation) {
				observer.observe(bottomNavigation);
				setBottomNavigationHeight(bottomNavigation.clientHeight);
			}

			const productBar = productBarRef.current;
			if (productBar) {
				observer.observe(productBar);
				setProductBarHeight(productBar.clientHeight);
			}

			const navigationBar = navigationBarRef.current;
			if (navigationBar) {
				observer.observe(navigationBar);
				setNavigationBarHeight(navigationBar.clientHeight);
			}

			return () => {
				observer.disconnect();
			};
		}, []);

		return (
			<>
				<ProductBar
					ref={productBarRef}
					sx={stickyProductHeaderSX.productBar({
						isProductBarVisible,
						bottomNavigationHeight,
					})}
				/>

				<NavigationBar
					ref={navigationBarRef}
					navigationBarHeight={navigationBarHeight}
					productBarHeight={productBarHeight}
					headerHeight={headerHeight}
					sx={stickyProductHeaderSX.navigationBar({
						isNavigationBarVisible,
						headerHeight,
						isProductBarVisible,
						productBarHeight,
					})}
				/>
			</>
		);
	}
);

StickyProductHeader.displayName = 'StickyProductHeader';
